// RoomScene — composites a framed artwork on top of an interior photo at the
// slot coordinates defined in roomScenes.js. Used by the gallery's In-situ
// toggle (compact card) and the detail page's room carousel (large preview).
//
// Key design: the room photo is rendered at its natural aspect inside the
// container with `object-contain`. Any leftover space is filled with the
// scene's `wallTone` so the photo never gets cropped — that way the slot
// coordinates (% of the image) always map to the correct wall position
// regardless of card size or container shape (square card vs 4/3 hero).

import { FRAME_STYLES } from "../lib/roomScenes";

function getFrame(frameId) {
  return FRAME_STYLES.find((f) => f.id === frameId) || FRAME_STYLES[0];
}

// v1.43 — parse the first number in a free-form dimensions string.
// Buyers write "H × W × D" (height first, e.g. "140 x 45 x 40 cm") so
// we take the FIRST number in the string and convert to cm based on
// whichever unit token appears in the string. Returns null when no
// number or no recognisable unit is present.
function parseHeightCm(dim) {
  if (!dim || typeof dim !== "string") return null;
  const s = dim.toLowerCase();
  const nums = s.match(/\d+(?:\.\d+)?/g);
  if (!nums || !nums.length) return null;
  const first = parseFloat(nums[0]);
  if (s.includes("cm") || s.includes("centimet")) return Math.round(first);
  if (/\b(?:in|inch|inches)\b|"/.test(s)) return Math.round(first * 2.54);
  if (/\b\d+(?:\.\d+)?\s*m\b/.test(s)) return Math.round(first * 100);
  return null;
}

// Categorise the sculpture by real height (fall back to "medium" if
// unknown so the visualiser always renders something sensible).
function sizeKind(heightCm) {
  if (!heightCm) return "medium";
  if (heightCm >= 80) return "large";
  if (heightCm >= 30) return "medium";
  return "small";
}

// Pick the surface (kind + on-screen colour / height / width) for a
// given scene + sculpture size. All surface heights are in real cm so
// the compositor can scale them against the 1.7 m human silhouette.
function surfaceFor(sceneId, kind, heightCm) {
  const cm = heightCm || 60;
  if (sceneId === "sculpture-museum") {
    // Museum: always a white plinth sized to bring the piece to eye level.
    // Total (plinth + sculpture) should be ~140-160 cm for a small piece,
    // ~120-160 cm for a medium, and floor-plinth (~15 cm base only) for a
    // large one so the sculpture still reads as monumental.
    const plinthCm =
      kind === "large" ? 15 :
      kind === "medium" ? Math.max(60, 130 - cm) :
      Math.max(90, 140 - cm);
    return {
      surfaceKind: "plinth",
      surfaceHeightCm: plinthCm,
      surfaceWidthPct: kind === "large" ? 18 : 14,
      surfaceColour: "#F6F3EA",
      surfaceShadow:
        "0 4px 10px rgba(0,0,0,0.25), 0 1px 2px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.5)",
      surfaceRadius: "0",
    };
  }
  if (sceneId === "sculpture-home") {
    if (kind === "large") {
      // Tall pieces stand on the floor — no surface.
      return { surfaceKind: "floor", surfaceHeightCm: 0, surfaceWidthPct: 24 };
    }
    if (kind === "medium") {
      // Medium — on a walnut sideboard at ~75 cm.
      return {
        surfaceKind: "sideboard",
        surfaceHeightCm: 75,
        surfaceWidthPct: 34,
        surfaceColour: "#5C3E28",
        surfaceShadow:
          "0 6px 12px rgba(0,0,0,0.30), 0 2px 3px rgba(0,0,0,0.20), inset 0 1px 0 rgba(255,255,255,0.10)",
        surfaceRadius: "0",
      };
    }
    // Small — on a floating oak shelf at ~140 cm.
    return {
      surfaceKind: "shelf",
      surfaceHeightCm: 140,
      surfaceWidthPct: 20,
      surfaceColour: "#B08858",
      surfaceShadow:
        "0 4px 8px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.20)",
      surfaceRadius: "0",
    };
  }
  // Outdoor
  if (kind === "large") {
    return { surfaceKind: "ground", surfaceHeightCm: 0, surfaceWidthPct: 24 };
  }
  if (kind === "medium") {
    // Low stone pedestal at ~40 cm.
    return {
      surfaceKind: "stone",
      surfaceHeightCm: 40,
      surfaceWidthPct: 20,
      surfaceColour: "#8A7F6E",
      surfaceShadow:
        "0 6px 10px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,0.10)",
      surfaceRadius: "2px",
    };
  }
  // Small on packed earth "mound" — very low so the piece reads as sited.
  return {
    surfaceKind: "stone",
    surfaceHeightCm: 15,
    surfaceWidthPct: 14,
    surfaceColour: "#7A6E5C",
    surfaceShadow: "0 4px 6px rgba(0,0,0,0.28)",
    surfaceRadius: "3px",
  };
}

export default function RoomScene({
  artworkUrl,
  artworkAlt = "",
  scene,
  frameId = "oak",
  className = "",
  loading = "lazy",
  style,
  // v1.41 — plinth-only interactive controls
  showScaleRef = false,   // draw a ~1.7m person silhouette next to the sculpture
  rotateDeg = 0,          // slider value (-60..+60) — see below
  // v1.43 — free-form dimensions string ("120 x 45 x 30 cm") drives
  // sculpture-in-scene scaling and surface choice.
  dimensions = "",
  // v1.44 — real gallery angles (fallback path only)
  angleImages = null,
  // v1.45 — photoreal Gemini composite URL. When present, the plinth
  // branch renders it as the full scene image (no synthetic surface,
  // no separate sculpture overlay) — silhouette + caption stay for
  // scale reference.
  compositeUrl = null,
}) {
  if (!scene) return null;
  const { image, slot, imageAspect, wallTone, type = "wall" } = scene;
  const frame = getFrame(frameId);
  const unframed = frame.id === "none";

  const innerStyle = {
    aspectRatio: String(imageAspect || 4 / 3),
    maxWidth: "100%",
    maxHeight: "100%",
    width: "100%",
  };

  // ---- SCREEN rendering (digital, video, generative) --------------- //
  // Renders the artwork inside a "digital display" — thin dark bezel,
  // soft screen glow, subtle top-highlight. Keeps object-cover so the
  // pixels fill the screen the way they would on a real monitor.
  if (type === "screen") {
    const bezel = 6;
    const screenStyle = {
      position: "absolute",
      top: `${slot.top}%`,
      left: `${slot.left}%`,
      width: `${slot.width}%`,
      height: `${slot.height}%`,
      backgroundColor: "#0f0f10",
      padding: `${bezel}px`,
      boxShadow: [
        "0 18px 30px rgba(0,0,0,0.28)",
        "0 4px 8px rgba(0,0,0,0.18)",
        "inset 0 1px 0 rgba(255,255,255,0.10)",
      ].join(", "),
      borderRadius: "3px",
    };
    return (
      <div
        className={`relative overflow-hidden flex items-center justify-center ${className}`}
        style={{ backgroundColor: wallTone || "#F4EFE6", ...(style || {}) }}
        data-testid={`room-scene-${scene.id}`}
        data-scene-type="screen"
      >
        <div className="relative" style={innerStyle}>
          <img
            src={image}
            alt={scene.label}
            className="absolute inset-0 w-full h-full object-cover"
            loading={loading}
          />
          <div style={screenStyle}>
            <img
              src={artworkUrl}
              alt={artworkAlt}
              className="block w-full h-full object-cover"
              loading={loading}
              style={{ filter: "brightness(1.02) saturate(1.05)" }}
            />
          </div>
        </div>
      </div>
    );
  }

  // ---- PLINTH rendering (sculptures / ceramics / curios) ----------- //
  // v1.43 — three realistic contexts (outdoor / museum / home). The
  // sculpture's real height in cm (parsed from `dimensions`) drives:
  //   1. Which surface it stands on:
  //        home:    tall → floor, medium → sideboard, small → shelf
  //        museum:  always on a white plinth sized to the piece
  //        outdoor: tall → ground, small → low stone
  //   2. Its display height relative to a virtual 1.7 m human
  //      silhouette so scale reads true regardless of the image aspect.
  if (type === "plinth") {
    const heightCm = parseHeightCm(dimensions);
    // v1.45 — photoreal composite short-circuit. When a composite URL is
    // supplied, the sculpture is already baked into the scene image, so
    // we just render it full-frame with the silhouette + caption on top.
    if (compositeUrl) {
      const compositeSrc = compositeUrl.startsWith("http")
        ? compositeUrl
        : `${process.env.REACT_APP_BACKEND_URL || ""}${compositeUrl}`;
      const SIL_H = 45;
      const GROUND = 78;
      const silhouetteStyle = {
        position: "absolute",
        top: `${GROUND - SIL_H}%`,
        height: `${SIL_H}%`,
        left: "6%",
        opacity: 0.22,
        pointerEvents: "none",
        zIndex: 2,
      };
      return (
        <div
          className={`relative overflow-hidden ${className}`}
          style={{ backgroundColor: wallTone || "#F4EFE6", ...(style || {}) }}
          data-testid={`room-scene-${scene.id}`}
          data-scene-type="plinth"
          data-composite="1"
        >
          <img
            src={compositeSrc}
            alt={`${artworkAlt} in ${scene.label}`}
            className="absolute inset-0 w-full h-full object-cover"
            loading={loading}
            data-testid="sculpture-composite-image"
          />
          {showScaleRef && (
            <svg
              viewBox="0 0 30 100"
              preserveAspectRatio="xMidYMax meet"
              style={silhouetteStyle}
              data-testid="plinth-scale-silhouette"
            >
              <circle cx="15" cy="10" r="5" fill="#111" />
              <path
                d="M15 15 L15 60 M15 25 L5 40 M15 25 L25 40 M15 60 L8 95 M15 60 L22 95"
                stroke="#111" strokeWidth="3" strokeLinecap="round" fill="none"
              />
            </svg>
          )}
          <div
            className="absolute bottom-2 right-2 bg-white/85 text-[#111] font-mono-aam text-[10px] px-2 py-0.5 tracking-wide"
            data-testid="sculpture-scale-caption"
          >
            {heightCm ? `${heightCm} cm tall` : "size on file"} · in {scene.label.split("·")[0].trim().toLowerCase()}
          </div>
        </div>
      );
    }
    // ---- fallback: v1.43 flat composite (surface + sculpture overlay) --
    const kind = sizeKind(heightCm);
    const placement = surfaceFor(scene.id, kind, heightCm);
    // Reference: silhouette height in inner-box %.
    // Ground line at y=78%, silhouette top at y=(78-45)=33% → 45% tall
    // representing 170 cm of real height (≈ 3.78 cm per 1 %).
    const SIL_H = 45;             // % of frame
    const GROUND = 78;            // % floor line
    const HUMAN_CM = 170;
    const pctPerCm = SIL_H / HUMAN_CM;
    // Surface (plinth/sideboard/shelf/stone) sits on the ground line.
    // Its own height in % is derived so that the sculpture's top lands
    // realistically relative to the human.
    const surfaceHeightPct = Math.min(60, placement.surfaceHeightCm * pctPerCm);
    const sculptureHeightPct = Math.min(
      GROUND - 4,               // never taller than the frame
      Math.max(4, heightCm * pctPerCm),
    );
    const surfaceTop = GROUND - surfaceHeightPct;
    const sculptureTop = surfaceTop - sculptureHeightPct;
    // Surface width — sideboard is wider than a plinth is wider than a shelf.
    const surfaceWidthPct = placement.surfaceWidthPct;
    const surfaceLeft = 50 - surfaceWidthPct / 2;
    const sculptureWidthPct = Math.min(surfaceWidthPct - 4, 34);
    const sculptureLeft = 50 - sculptureWidthPct / 2;

    // Silhouette — bottom on ground, offset to the left so it doesn't
    // overlap the sculpture.
    const silhouetteStyle = {
      position: "absolute",
      top: `${GROUND - SIL_H}%`,
      height: `${SIL_H}%`,
      left: "8%",
      opacity: 0.25,
      pointerEvents: "none",
      zIndex: 2,
    };

    // Grounding shadow directly under the surface (or sculpture on floor).
    const shadowStyle = {
      position: "absolute",
      top: `${GROUND - 1.4}%`,
      left: `${surfaceLeft + 2}%`,
      width: `${surfaceWidthPct - 4}%`,
      height: "2.6%",
      background:
        "radial-gradient(ellipse at center, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.22) 50%, rgba(0,0,0,0) 78%)",
      filter: "blur(2px)",
      pointerEvents: "none",
      zIndex: 1,
    };

    // Surface rectangle (skip if placement is "floor" — sculpture sits
    // directly on the ground line).
    const surfaceStyle = placement.surfaceHeightCm > 0 ? {
      position: "absolute",
      top: `${surfaceTop}%`,
      left: `${surfaceLeft}%`,
      width: `${surfaceWidthPct}%`,
      height: `${surfaceHeightPct}%`,
      backgroundColor: placement.surfaceColour,
      boxShadow: placement.surfaceShadow,
      zIndex: 2,
      borderRadius: placement.surfaceRadius || "0",
    } : null;

    // Sculpture image — bottom-aligned on the surface (or ground).
    // v1.44 — if we have real gallery angles, map slider to an image
    // index; otherwise fall back to a CSS rotateY on the single image.
    const hasAngles = Array.isArray(angleImages) && angleImages.length >= 2;
    let displayUrl = artworkUrl;
    let angleIdx = 0;
    if (hasAngles) {
      // Map -60..+60 → 0..N-1 with the middle of the slider showing
      // the "front" (index 0). Negative rotates left through angles
      // in the second half of the array; positive rotates right
      // through the first half of the array — mirrored for intuition.
      const N = angleImages.length;
      const norm = (rotateDeg + 60) / 120; // 0..1
      angleIdx = Math.min(N - 1, Math.max(0, Math.round(norm * (N - 1))));
      displayUrl = angleImages[angleIdx];
    }
    const sculptureStyle = {
      position: "absolute",
      top: `${sculptureTop}%`,
      left: `${sculptureLeft}%`,
      width: `${sculptureWidthPct}%`,
      height: `${sculptureHeightPct}%`,
      transform: hasAngles ? "none" : `rotateY(${rotateDeg}deg)`,
      transformOrigin: "50% 100%",
      transition: hasAngles ? "opacity 0.12s linear" : "transform 0.15s linear",
      filter:
        "drop-shadow(0 6px 8px rgba(0,0,0,0.28)) drop-shadow(0 2px 3px rgba(0,0,0,0.16))",
      zIndex: 3,
    };

    return (
      <div
        className={`relative overflow-hidden flex items-center justify-center ${className}`}
        style={{ backgroundColor: wallTone || "#F4EFE6", ...(style || {}) }}
        data-testid={`room-scene-${scene.id}`}
        data-scene-type="plinth"
        data-sculpture-size={kind}
      >
        <div className="relative" style={innerStyle}>
          <img
            src={image}
            alt={scene.label}
            className="absolute inset-0 w-full h-full object-cover"
            loading={loading}
          />
          <div style={shadowStyle} data-testid="plinth-ground-shadow" />
          {surfaceStyle && (
            <div
              style={surfaceStyle}
              data-testid={`sculpture-surface-${placement.surfaceKind}`}
            />
          )}
          {showScaleRef && (
            <svg
              viewBox="0 0 30 100"
              preserveAspectRatio="xMidYMax meet"
              style={silhouetteStyle}
              data-testid="plinth-scale-silhouette"
            >
              {/* Minimalist standing figure — 1.7 m implied */}
              <circle cx="15" cy="10" r="5" fill="#111" />
              <path
                d="M15 15 L15 60 M15 25 L5 40 M15 25 L25 40 M15 60 L8 95 M15 60 L22 95"
                stroke="#111" strokeWidth="3" strokeLinecap="round" fill="none"
              />
            </svg>
          )}
          <img
            src={displayUrl}
            alt={artworkAlt}
            style={{ ...sculptureStyle, objectFit: "contain" }}
            loading={loading}
            data-testid="sculpture-image"
            data-angle-index={angleIdx}
          />
          {/* Real-height caption in the corner for buyer confidence */}
          <div
            className="absolute bottom-2 right-2 bg-white/85 text-[#111] font-mono-aam text-[10px] px-2 py-0.5 tracking-wide"
            data-testid="sculpture-scale-caption"
          >
            {heightCm ? `${heightCm} cm tall` : "size unknown"} · {placement.surfaceKind}
            {hasAngles && ` · view ${angleIdx + 1}/${angleImages.length}`}
          </div>
        </div>
      </div>
    );
  }

  // ---- WALL rendering (paintings, prints, drawings, photos) --------- //
  const frameWrapper = {
    position: "absolute",
    top: `${slot.top}%`,
    left: `${slot.left}%`,
    width: `${slot.width}%`,
    height: `${slot.height}%`,
  };

  const frameStyle = unframed
    ? {
        ...frameWrapper,
        boxShadow:
          "0 8px 18px rgba(0,0,0,0.18), 0 2px 4px rgba(0,0,0,0.12)",
      }
    : {
        ...frameWrapper,
        backgroundColor: frame.mat,
        boxShadow: [
          "0 14px 26px rgba(0,0,0,0.22)",
          "0 6px 10px rgba(0,0,0,0.16)",
          `0 0 0 1px ${frame.mouldingDark}`,
        ].join(", "),
        padding: `clamp(3px, ${frame.matPx / 5}px, ${frame.matPx}px)`,
        border: `clamp(2px, ${frame.mouldingPx / 5}px, ${frame.mouldingPx}px) solid ${frame.moulding}`,
      };

  return (
    <div
      className={`relative overflow-hidden flex items-center justify-center ${className}`}
      style={{ backgroundColor: wallTone || "#F4EFE6", ...(style || {}) }}
      data-testid={`room-scene-${scene.id}`}
      data-scene-type="wall"
    >
      <div className="relative" style={innerStyle}>
        <img
          src={image}
          alt={scene.label}
          className="absolute inset-0 w-full h-full object-cover"
          loading={loading}
        />
        <div style={frameStyle}>
          <img
            src={artworkUrl}
            alt={artworkAlt}
            className="block w-full h-full object-cover"
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
}
